import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';

const SLIDE_OFFSET = 28;
const AUTO_INTERVAL_MS = 5000;

const slideVariants = {
  enter: (direction) => ({
    y: direction > 0 ? SLIDE_OFFSET : -SLIDE_OFFSET,
    opacity: 0,
  }),
  center: {
    y: 0,
    opacity: 1,
  },
  exit: (direction) => ({
    y: direction > 0 ? -SLIDE_OFFSET : SLIDE_OFFSET,
    opacity: 0,
  }),
};

function StateSlideCarousel({
  items = [],
  renderItem,
  intervalMs = AUTO_INTERVAL_MS,
  className = '',
}) {
  const prefersReducedMotion = useReducedMotion();
  const [[index, direction], setSlide] = useState([0, 1]);
  const pauseUntilRef = useRef(0);
  const wheelLockRef = useRef(false);

  const count = items.length;
  const currentIndex = count > 0 ? ((index % count) + count) % count : 0;

  const paginate = useCallback(
    (nextDirection) => {
      if (count <= 1) return;
      setSlide(([prevIndex]) => [prevIndex + nextDirection, nextDirection]);
      pauseUntilRef.current = Date.now() + intervalMs;
    },
    [count, intervalMs]
  );

  useEffect(() => {
    if (count <= 1 || prefersReducedMotion) return undefined;

    const id = window.setInterval(() => {
      if (Date.now() < pauseUntilRef.current) return;
      paginate(1);
    }, intervalMs);

    return () => window.clearInterval(id);
  }, [count, intervalMs, paginate, prefersReducedMotion]);

  const handleDragEnd = (_, info) => {
    const threshold = 40;
    if (info.offset.y < -threshold || info.velocity.y < -300) {
      paginate(1);
    } else if (info.offset.y > threshold || info.velocity.y > 300) {
      paginate(-1);
    }
  };

  const handleWheel = (event) => {
    if (count <= 1 || wheelLockRef.current) return;
    if (Math.abs(event.deltaY) < 12) return;

    event.preventDefault();
    wheelLockRef.current = true;
    window.setTimeout(() => {
      wheelLockRef.current = false;
    }, 400);

    if (event.deltaY > 0) {
      paginate(1);
    } else {
      paginate(-1);
    }
  };

  if (count === 0) {
    return null;
  }

  const currentItem = items[currentIndex];

  return (
    <div className={`relative flex w-full items-center justify-center ${className}`}>
      <div
        className="relative w-full overflow-hidden touch-pan-y"
        onWheel={handleWheel}
      >
        <AnimatePresence initial={false} custom={direction} mode="popLayout">
          <motion.div
            key={currentItem.key ?? currentIndex}
            custom={direction}
            variants={prefersReducedMotion ? undefined : slideVariants}
            initial={prefersReducedMotion ? false : 'enter'}
            animate="center"
            exit={prefersReducedMotion ? undefined : 'exit'}
            transition={
              prefersReducedMotion
                ? { duration: 0 }
                : { type: 'spring', stiffness: 380, damping: 32 }
            }
            drag={count > 1 ? 'y' : false}
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={0.12}
            onDragEnd={handleDragEnd}
            className="w-full cursor-grab active:cursor-grabbing"
          >
            {renderItem(currentItem, currentIndex)}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}

export default StateSlideCarousel;
